/** Falling pixels for the birthday page.
 *
 *  DOM elements and a CSS keyframe, not canvas and not requestAnimationFrame.
 *  Forty absolutely-positioned squares with a transform animation composite on
 *  the GPU and cost no JavaScript per frame; a canvas loop would burn a phone's
 *  battery on the one screen where nothing has loaded yet. It also means
 *  `prefers-reduced-motion` can switch the whole thing off in CSS, with no
 *  matchMedia -- which jsdom does not have.
 *
 *  Seeded, so the same page renders the same confetti twice: a failed password
 *  attempt re-renders this component, and reshuffling every square under the
 *  reader's eyes would read as a glitch rather than as a wrong password.
 */

/** Minecraft's own blocks: grass, dirt, stone, redstone, gold, diamond, lapis. */
export const CONFETTI_COLOURS = [
  "#5b8731", "#866043", "#7f7f7f", "#d33b3b", "#fcdb05", "#4aedd9", "#3c44aa",
] as const;

export interface Piece {
  left: number;    // vw, where it falls
  size: number;    // px, square
  delay: number;   // s, before it starts
  dur: number;     // s, top to bottom
  drift: number;   // px, sideways travel on the way down
  spin: number;    // deg, total rotation
  colour: string;
}

/** Bruce Forstall's mulberry32. Thirty-two bits of state, uniform enough for
 *  scattering squares, and short enough to read -- the point is that it is
 *  deterministic, not that it is a good PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function confettiPieces(n: number, seed = 25): Piece[] {
  const rnd = mulberry32(seed);
  const pieces: Piece[] = [];
  for (let i = 0; i < n; i++) {
    pieces.push({
      left: rnd() * 100,
      size: 6 + Math.round(rnd() * 8),
      // Spread the start times over a full fall, so the screen is already
      // raining when you arrive rather than filling from the top.
      delay: Number((rnd() * 9).toFixed(2)),
      dur: Number((6 + rnd() * 7).toFixed(2)),
      drift: Math.round((rnd() - 0.5) * 120),
      spin: Math.round((rnd() - 0.5) * 720),
      colour: CONFETTI_COLOURS[Math.floor(rnd() * CONFETTI_COLOURS.length)],
    });
  }
  return pieces;
}
